import React, { useEffect } from 'react';
import { Dropdown, Icon } from '@statsbomb/kitbag-components';
import { useQuery, useReactiveVar, gql, useLazyQuery } from '@apollo/client';
import { isEmpty, isEqual } from 'lodash';
// eslint-disable-next-line import/no-unresolved
import { Radar } from '@statsbomb/kitbag-datavis';
import { useTheme } from 'styled-components';
import {
  SummaryTile,
  SummaryTileBody,
  SummaryTileCol,
  SummaryTileFooter,
  SummaryTileHeader,
  SummaryError,
} from '../../../components/Tile/TileSummary.styles';
import { mf_TeamDetails } from '../../../apollo';
import { formatTemplateStatsAsRadarAxes } from '../../player/PlayerRadar/PlayerRadar.dataManipulation';
import { buildRadarData } from '../../player/PlayerOverview/PlayerRadar.Tile.helpers';
import useQueryString from '../../../utils/hooks/useQueryString';
import { ERROR_CODES } from '../../../utils/errorCodes/errorCodes';
import {
  OptionWithIcon,
  SingleValueWithIcon,
} from '../../../components/DropdownFilter/DropdownFilter.components';
import { RadarTileContainer } from '../../player/PlayerOverview/PlayerRadar.Tile.styles';
import {
  getTeamRadarStatsQuery,
  getTeamRadarStatsLeagueAverageQuery,
} from './getTeamRadarStats';
import { useGetTemplateDefinitionsTeam } from '../TeamRadar/TeamRadar.hooks';
import { TEAM_RADAR_CHART_OPTIONS } from './TeamRadar.Tile.constants';
import GoTo from '../../../components/buttons/GoTo';
import { DropdownWrapper } from '../../../components/DropdownFilter/Dropdown.styles';
import LoaderInline from '../../../components/LoaderInline/LoaderInline';
import Dimmer from '../../../components/Dimmer/Dimmer';
import SimpleRadarKey from '../../../visualisations/RadarChart/SimpleRadarKey';
import { getPalette } from '../../../utils/visualisations/visPalettes';
import { useStore } from '../../../store/useStore';

const loadingRadarMsg = (
  isConfigLoading,
  isOffenseStatsLoading,
  isDefenseStatsLoading,
  statsLoadingLA
) => {
  if (isConfigLoading) {
    return 'Loading Templates';
  }

  if (isOffenseStatsLoading) {
    return 'Loading Team Offense Stats';
  }

  if (isDefenseStatsLoading) {
    return 'Loading Team Defense Stats';
  }

  if (statsLoadingLA) {
    return 'Loading League Average Data';
  }

  return '';
};

const TeamRadarTile = function () {
  const mf_TeamDetailsRV = useReactiveVar(mf_TeamDetails);
  const [showLeagueAverage, setShowLeagueAverage] = useQueryString(
    'avg',
    false
  );
  const { isDark } = useTheme();
  const visPalette = getPalette(isDark);

  const teamColors = {
    primary: mf_TeamDetailsRV?.colorPrimary,
    secondary: mf_TeamDetailsRV?.colorSecondary,
    tertiary: mf_TeamDetailsRV?.colorTertiary,
  };

  const {
    templates,
    loading: isConfigLoading,
    error: configError,
  } = useGetTemplateDefinitionsTeam();

  const selectedTemplate = templates?.find((t) => t.name === 'Offense');
  const selectedTemplateDefense = templates?.find((t) => t.name === 'Defense');
  const templateNames = selectedTemplate?.stats?.map((m) => m.name);
  const templateNamesDefense = selectedTemplateDefense?.stats?.map(
    (m) => m.name
  );
  const templateNamesLA = selectedTemplate?.stats
    ?.concat(selectedTemplateDefense?.stats)
    ?.map((m) => m.name);

  const teamOffenseStatsGQLString = getTeamRadarStatsQuery(templateNames, true);
  const teamDefenseStatsGQLString = getTeamRadarStatsQuery(
    templateNamesDefense,
    false
  );
  const teamStatsLAGQLString =
    getTeamRadarStatsLeagueAverageQuery(templateNamesLA);

  const [
    getDefense,
    {
      data: dataDefenseStats,
      loading: isDefenseStatsLoading,
      error: defenseStatsError,
    },
  ] = useLazyQuery(gql(teamDefenseStatsGQLString), {
    context: {
      headers: {
        'Accept-Version': 'v2023q2',
      },
    },
  });
  if (defenseStatsError) {
    console.error(ERROR_CODES.GET_TEAM_RADAR_DEFENSE_STATS, defenseStatsError);
  }

  const {
    data: dataOffenseStats,
    loading: isOffenseStatsLoading,
    error: offenseStatsError,
  } = useQuery(gql(teamOffenseStatsGQLString), {
    context: {
      headers: {
        'Accept-Version': 'v2023q2',
      },
    },
    onCompleted: (drivesData) => {
      if (drivesData?.teamStats?.items.length > 0) {
        getDefense();
      }
    },
  });
  if (offenseStatsError) {
    console.error(ERROR_CODES.GET_TEAM_RADAR_OFFENSE_STATS, offenseStatsError);
  }

  const isError = !!offenseStatsError || !!configError || !!defenseStatsError;
  const isLoading =
    isConfigLoading || isOffenseStatsLoading || isDefenseStatsLoading;

  const {
    data: dataStatsLA,
    loading: statsLoadingLA,
    error: statsLAError,
  } = useQuery(gql(teamStatsLAGQLString), {
    skip: !showLeagueAverage,
    context: {
      headers: {
        'Accept-Version': 'v2023q2',
      },
    },
  });
  if (statsLAError) {
    console.error(ERROR_CODES.GET_RADAR_STATS_LA, statsLAError);
  }

  const radarLAComparison =
    showLeagueAverage && dataStatsLA?.teamStats?.items[0];
  const offenseStats = dataOffenseStats?.teamStats?.items?.[0];
  const defenseStats = dataDefenseStats?.teamStats?.items?.[0];

  const templateConfigOffense = formatTemplateStatsAsRadarAxes(
    selectedTemplate?.stats
  );
  const templateConfigDefense = formatTemplateStatsAsRadarAxes(
    selectedTemplateDefense?.stats
  );

  const radarOffenseData = useStore(
    (state) => state.teamOverviewRadarOffenseData
  );
  const setRadarOffenseData = useStore(
    (state) => state.setTeamOverviewRadarOffenseData
  );
  const radarDefenseData = useStore(
    (state) => state.teamOverviewRadarDefenseData
  );
  const setRadarDefenseData = useStore(
    (state) => state.setTeamOverviewRadarDefenseData
  );

  useEffect(() => {
    if (!isEmpty(offenseStats)) {
      const nextOffenseData = buildRadarData(
        offenseStats,
        templateConfigOffense,
        radarLAComparison
      );

      if (!isEqual(nextOffenseData, radarOffenseData)) {
        setRadarOffenseData(
          offenseStats,
          templateConfigOffense,
          radarLAComparison
        );
      }
    }
  }, [
    offenseStats,
    templateConfigOffense,
    radarLAComparison,
    radarOffenseData,
  ]);

  useEffect(() => {
    if (!isEmpty(defenseStats)) {
      const nextDefenseData = buildRadarData(
        defenseStats,
        templateConfigDefense,
        radarLAComparison
      );

      if (!isEqual(nextDefenseData, radarDefenseData)) {
        setRadarDefenseData(
          defenseStats,
          templateConfigDefense,
          radarLAComparison
        );
      }
    }
  }, [
    defenseStats,
    templateConfigDefense,
    radarLAComparison,
    radarDefenseData,
  ]);

  return (
    <SummaryTile>
      <SummaryTileHeader>
        <div>
          <h3>Radars</h3>
        </div>
        <div>
          {!isLoading && !isError && (
            <DropdownWrapper>
              <Dropdown
                id="team-radar-tile-dropdown"
                components={{
                  Option: OptionWithIcon,
                  SingleValue: SingleValueWithIcon,
                }}
                isSearchable={false}
                menuAlignment="right"
                menuPlacement="bottom"
                onChange={(item) => setShowLeagueAverage(item.value === 1)}
                options={TEAM_RADAR_CHART_OPTIONS}
                value={TEAM_RADAR_CHART_OPTIONS[showLeagueAverage ? 1 : 0]}
                labelPosition="none"
                label="Team radar display options"
                variant="tertiary"
                size="small"
              />
            </DropdownWrapper>
          )}
          <GoTo
            href={`/team/radar/:leagues/:seasons/:teams${
              showLeagueAverage ? '?avg=true' : ''
            }`}
            linkName="Open Team Radar"
            iconSize="small"
          />
        </div>
      </SummaryTileHeader>
      <SummaryTileBody>
        {isLoading && <Dimmer active style={{ maxWidth: '100%' }} />}
        {isError && !isLoading && !statsLoadingLA && (
          <SummaryError>
            <Icon size="small" variant="Warning" colour="primary.main" />
            There has been an error.
          </SummaryError>
        )}
        <div>
          <RadarTileContainer $maxHeight="450px">
            <div>
              <p>Offense</p>
              {!isEmpty(radarOffenseData) && (
                <Radar
                  id="radar-offense"
                  data={radarOffenseData}
                  primaryColour={teamColors?.primary}
                  secondaryColour={teamColors?.secondary}
                  comparisonColour={teamColors?.secondary}
                  centreCircle="small"
                  width={450}
                />
              )}

              <SimpleRadarKey
                visPalette={visPalette}
                primaryColor={teamColors?.primary}
                secondaryColor={teamColors?.secondary}
                fontScaler={0.6}
                subjectLabel="Team"
                width={230}
                height={25}
                opacity={showLeagueAverage ? 1 : 0}
              />
            </div>
            <div>
              <p>Defense</p>
              {!isEmpty(radarDefenseData) && (
                <Radar
                  id="radar-defense"
                  data={radarDefenseData}
                  primaryColour={teamColors?.primary}
                  secondaryColour={teamColors?.secondary}
                  comparisonColour={teamColors?.secondary}
                  centreCircle="small"
                  width={450}
                />
              )}

              <SimpleRadarKey
                visPalette={visPalette}
                primaryColor={teamColors?.primary}
                secondaryColor={teamColors?.secondary}
                fontScaler={0.6}
                subjectLabel="Team"
                width={230}
                height={25}
                opacity={showLeagueAverage ? 1 : 0}
              />
            </div>
          </RadarTileContainer>
        </div>
      </SummaryTileBody>
      <SummaryTileFooter $transparentBackground={!isLoading && !statsLoadingLA}>
        <SummaryTileCol>
          {(isLoading || statsLoadingLA) && (
            <LoaderInline
              text={loadingRadarMsg(
                isConfigLoading,
                isOffenseStatsLoading,
                isDefenseStatsLoading,
                statsLoadingLA
              )}
            />
          )}
        </SummaryTileCol>
        <SummaryTileCol />
      </SummaryTileFooter>
    </SummaryTile>
  );
};

export default TeamRadarTile;
